import { unlayerDesign } from '../../unlayer/UnlayerDesignProcessor';
import { convertFromUnlayerFieldToCustomFormField } from '../../unlayer/UnlayerFormConverters';
import { FORM_TYPE_LANDING_PAGE, KEAP_TOOLS, DEFAULT_LANDINGPAGE_FORM } from './unlayer.constants';
import {
    createMarketingForm,
    getMarketingForm,
    getMarketingFormBySlug,
    updateMarketingForm,
} from './marketingSiteEditorApi';

/**
         * Converts all unlayer forms to keap forms, and creates a corresponding record in smart-forms-api
         * if one doesn't already exist.
         * @param {function} formNameGenerator
         * @param {string} formNamePrefix
         * @param page
         * @param design
         *
         * @return {Promise<object>}
         */
export const updateCustomFormsWithinPage = async ({
    formNamePrefix, formNameGenerator, page, design,
}) => {
    const designHelper = unlayerDesign(design);

    await Promise.all(designHelper.findByType(KEAP_TOOLS.keapForm)
        .map(async (cell) => {
            const { content } = cell;
            const updatedForm = await updateCustomFormFromUnlayerForm({
                page,
                content,
                formNamePrefix,
                defaultFormName: formNameGenerator(),
            });

            Object.assign(content, updatedForm);

            return updatedForm;
        }));

    return designHelper.design;
};

/**
 * Takes in the unlayer tool configuration values for a keap_forms block, and creates or updates
 * a corresponding record in the smart-form-api.  This record id is stored in the tool configuration
 * under the key "keapForm" along with other meaningful data.
 *
 * It's important that this value is set _before_ unlayer renders the markup for the form.  Otherwise,
 * the final page will not have the correct URL to submit to.
 *
 * @param input The values passed from unlayer
 * @param input.content The configuration values for the form
 * @param input.defaultFormName The title of the smart-form
 * @param input.formNamePrefix The hard-coded prefix for the form name
 * @param input.description The description of the smart-form
 * @return {Promise<*>} The modified copy of the original content
 */
export const updateCustomFormFromUnlayerForm = async ({
    page, content = {}, defaultFormName, formNamePrefix, description = '',
} = {}) => {
    const { values = {}, ...rest } = content;
    const { fields = [] } = values;

    let { keapForm = {} } = values;
    let { formName } = values;

    formName = formName ?? defaultFormName;
    const title = `${formNamePrefix} - ${formName}`;

    keapForm.title = title;
    keapForm.fields = fields;

    if (keapForm.slug) {
        if (!keapForm?.id) {
            // Try to find the form with a matching slug first.  It may have been created previously, but
            // the unlayer document just hasn't been synced up yet.
            let bySlug;

            try {
                bySlug = await loadCustomFormBySlug(keapForm.slug);
            } catch (e) {
                bySlug = null;
            }

            if (bySlug) {
                keapForm.id = bySlug.id;
            } else {
                // The form doesn't appear to exist, so let's create it using the generated slug
                const {
                    id, formType, accountId, slug,
                } = (await createCustomForm({
                    title,
                    fields: fields.map(convertFromUnlayerFieldToCustomFormField).filter((field) => field),
                    description,
                    slug: keapForm.slug,
                    pageId: page.id,
                }) ?? {});

                if (id) {
                    keapForm = {
                        ...keapForm, id, formId: id, formType, title, accountId, slug,
                    };

                    values.keapForm = keapForm;
                }
            }
        }

        if (keapForm.id) {
            const savedForm = await loadCustomForm(keapForm.id);

            // Update the form with the fields and name
            savedForm.data.fields = fields.map(convertFromUnlayerFieldToCustomFormField).filter((field) => field);
            savedForm.data.title = title;
            savedForm.data.description = description;
            savedForm.data.formType = FORM_TYPE_LANDING_PAGE;
            savedForm.data.pageId = page.id;

            const response = await updateCustomForm(savedForm.data);

            keapForm = Object.assign(response.data, { formId: response.data.id });
        }
    }

    return {
        values: {
            ...values,
            keapForm,
            formName,
        },
        ...rest,
    };
};
/**
 * Creates a new custom form
 * @param title
 * @param description
 * @param slug
 * @return {Promise<object>}
 */

export const createCustomForm = async ({
    formId, formSource, formType, name, pageId, title, fields, description, slug,
}) => {
    const postData = {
        ...DEFAULT_LANDINGPAGE_FORM,
        formId,
        formSource,
        formType,
        name,
        pageId,
        title,
        fields,
        description,
        slug,
    };

    const { data: savedForm } = await createMarketingForm(postData);

    return savedForm;
};

/**
 * Loads a custom form by id
 * @param formId
 * @return {Promise<object>}
 */
export const loadCustomForm = async (formId) => {
    const form = await getMarketingForm(formId);

    return form;
};

/**
 * Updates a custom form
 * @param formToSave
 * @return {Promise<object>}
 */
export const updateCustomForm = async (formToSave) => {
    const savedForm = await updateMarketingForm(formToSave.id, formToSave);

    return savedForm;
};

export async function loadCustomFormBySlug(slug) {
    try {
        const { data } = await getMarketingFormBySlug(slug);

        return data;
    } catch (e) {
        return null;
    }
}
